export const enum RetrievalMode {
    Hybrid = "hybrid",
    Vectors = "vectors",
    Text = "text"
}

export const enum SpeechRate {
    XSlow = "x-slow",
    Slow = "slow",
    Medium = "medium",
    Fast = "fast",
    XFast = "x-fast"
}

export const enum Environment {
    Sandbox = "sandbox",
    Dev = "dev",
    Production = "production"
}

export type Thoughts = {
    title: string;
    description: any; // It can be any output from the api
    props?: { [key: string]: any };
};

export type Source = {
    citationId: string;
    category: string;
    chapter: string | null;
    content: string;
    source: string;
    cloudinaryID: string;
    title: string;
    id: string;
    interestAreas: string[];
    language: string;
    mediaType: string;
    resourceTypes: string[];
    storageUrl: string;
    sourcefile: string;
    sourcepage: string;
    isMergedChunk: boolean;
    score: number;
    reranker_score: number;
    lastModifiedTimestamp: string | null;
    thumbnailUrl: string;
    referenceNumber: string;

    publicationId: string | null;
    publicationPubMedId: string | null;
    publicationPublicationYear: string | null;
    pulicationAuthors: string | null;
    pulicationStudyType: string | null;
    publicationBodySite: string | null;
    publicationType: string | null;
    publicationJournal: string | null;
    publicationJournalImpactFactor: number | null;
    publicationJournalSCImagoJournalRank: number | null;
    publicationJournalSourceNormalizedImpactperPaper: number | null;
    publicationUrl: string | null;
    publicationAiConsent: boolean | null;
    publicationIndications: string[] | null;
    publicationTechniques: string[] | null;
    publicationProductGroups: string[] | null;
    publicationPubMedAbstract: string | null;

    intellumDescription: string | null;
    intellumTranscriptId: string | null;
    intellumOrganization: string | null;
    intellumPresenters: string | null;
    intellumVideoUrl: string | null;
    intellumUrl: string | null;
};

export type BuildInfo = {
    gitHash: string;
    branch: string;
    buildTime: string;
    environment: string;
};

export type Config = {
    googleTagManagerId?: string;
    defaultReasoningEffort: string;
    showSpeechInput: boolean;
    showSpeechOutputBrowser: boolean;
    showSpeechOutputAzure: boolean;
    showChatHistoryBrowser: boolean;
    showChatHistoryCosmos: boolean;
    environment: Environment;
};

export type SimpleAPIResponse = {
    message?: string;
};

export interface SpeechConfig {
    speechUrls: (string | null)[];
    setSpeechUrls: (urls: (string | null)[]) => void;
    audio: HTMLAudioElement;
    isPlaying: boolean;
    setIsPlaying: (isPlaying: boolean) => void;
}

export type HistoryApiResponse = {
    id: string;
    name: string;
    username: string;
    /** Absent on conversations stored before email was recorded, empty once anonymized. */
    email?: string;
    entra_oid: string;
    title: string;
    answers: V2ChatHistoryTurn[];
    timestamp: number;
};

export type UserSettingsApiResponse = {
    id: string;
    entra_oid: string;
    name: string;
    username: string;
    userCountry: string;
    settings: {
        [key: string]: any;
    };
};

export type AuthorizationApiResponse = {
    isAdmin: boolean;
};

export type FeedbackListApiResponse = {
    feedback: [];
    name: string;
    username: string;
    total_items: number;
    total_pages: number;
    page: number;
    page_size: number;
};

export type FeedbackItemApiResponse = {
    id: string;
    entra_oid: string;
    name: string;
    username: string;
    /** Absent on feedback stored before email was recorded. */
    email?: string;
    conversation_id?: string;
    turn_index: number;
    user_country?: string;
    positive: boolean;
    answers: V2ChatHistoryTurn[];
    timestamp: string;
    jira_ticket?: string | null;
    review_status?: "reviewed" | null;
    failure_type?: string | null;
    reason?: string | null;
    comment?: string | null;
};

export type IdeaItem = {
    id: string;
    entra_oid: string;
    name: string;
    username: string;
    conversation_id: string;
    idea: string;
    category: string;
    timestamp: number;
    status: "confirmed" | "draft";
    /** Absent on ideas stored before review status existed; treated as "new". */
    review_status?: IdeaReviewStatus | null;
    jira_link?: string | null;
    review_comment?: string | null;
};

export type IdeaReviewStatus = "new" | "reviewed" | "cancelled";

export type IdeaListApiResponse = {
    ideas: IdeaItem[];
    total_items: number;
    total_pages: number;
    page: number;
    page_size: number;
};

export type ChatHistoryListItem = {
    id: string;
    name: string;
    username: string;
    title: string;
    timestamp: number;
    messages_count?: number;
};

export type ChatHistoryListApiResponse = {
    history: ChatHistoryListItem[];
    total_items: number;
    total_pages: number;
    page: number;
    page_size: number;
};

// Analytics Types
export type TouchpointAnalyticsData = {
    touchpoint: string;
    message_count: number;
    percentage: number;
    unique_users: number;
};

export type TouchpointAnalyticsResponse = {
    data: TouchpointAnalyticsData[];
    start_date: string;
    end_date: string;
    total_messages: number;
    total_touchpoints: number;
    available_countries: string[];
    available_domains?: string[];
    sql_query?: string;
};

// Azure AI Search Admin Types
export type SearchAdminRequest = {
    query_text: string;
    reference_number: string;
    top?: number;
    merge_chunks?: boolean;
    use_text_search?: boolean;
    use_vector_search?: boolean;
    use_semantic_ranker?: boolean;
    use_semantic_captions?: boolean;
    minimum_search_score?: number;
    minimum_reranker_score?: number;
    use_query_rewriting?: boolean;
    selected_countries?: string[];
    domain?: Domain;
    sources?: string[];
    asset_types?: string[];
    interest_areas?: string[];
    detected_interest_areas?: string[];
    overrides?: any;
    source_file?: string;
    cloudinary_id?: string;
};

export type SearchAdminResponse = {
    results: Source[];
    total_results: number;
    query_text: string;
    filter: string | null;
    search_parameters: {
        top: number;
        use_text_search: boolean;
        use_vector_search: boolean;
        use_semantic_ranker: boolean;
        use_semantic_captions: boolean;
        minimum_search_score: number | null;
        minimum_reranker_score: number | null;
        use_query_rewriting: boolean;
    };
};

export interface AssetTypeOption {
    id: string;
    name: string;
    group?: string;
}

export interface InterestAreaOption {
    key: string;
    name: string;
}

export interface FilterOptionsApiResponse {
    assetTypes: AssetTypeOption[];
    interestAreas: InterestAreaOption[];
}

export interface ExampleQuestionsApiResponse {
    questions: { question: string }[];
}

// ── v2 API types ──────────────────────────────────────────────────────────────

export type V2Source = {
    id: string;
    title: string;
    dataSource: string;
    mediaType: string;
    url: string;
    assetUrl: string | null;
    lastModifiedOn?: string | null;
    rankingTimestamp?: string | null;
    resourceType?: string | null;
    cloudinaryId?: string | null;
    referenceNumber?: string | null;
    thumbnailUrl?: string | null;
    interestAreas?: string[] | null;
    searchScore?: number | null;
    rerankerSearchScore?: number | null;
};

export type V2Thought = {
    title: string;
    description?: string | null;
    props?: { [key: string]: any } | null;
};

export type V2ToolCall = {
    id: string;
    status: string;
    statusValue: "running" | "completed" | "failed";
    toolName: string;
    toolId: string;
    arguments?: string | null;
    results?: any;
};

export type V2Delta = {
    id?: string | null;
    role?: string;
    phiDetected?: boolean;
    response?: string | null;
    toolCalls?: V2ToolCall[] | null;
    sources?: V2Source[] | null;
    thoughts?: V2Thought[] | null;
    followUpQuestions?: string[] | null;
    error?: string | null;
    errorDetails?: ErrorDetails | null;
};

export type V2StreamErrorCode = "rate_limit" | "content_filter" | "content_length" | "generic";

export type ErrorDetails = {
    code: V2StreamErrorCode;
};

export type Domain = "human" | "vet";

export type V2ChatRequest = {
    message: string;
    selectedCountries: string[];
    domain: Domain;
    dataSources?: string[] | null;
    userFilters?: {
        assetTypes: string[];
        interestAreas: string[];
    } | null;
    id?: string | null;
    sstRequestIds?: string[];
};

export type V2ChatHistoryItem = {
    id: string;
    title: string;
    timestamp: number;
};

export type V2ChatHistoryListApiResponse = {
    items: V2ChatHistoryItem[];
    continuationToken: string | null;
};

export type V2ChatHistoryTurn = {
    userMessage: string;
    response: V2Delta;
    // The domain selector can be toggled between turns, so it is recorded per turn.
    domain: Domain;
    // Speech-to-text request ids of the dictations behind this question. Only returned by admin endpoints.
    sstRequestIds?: string[];
};

export type V2ChatHistoryApiResponse = {
    id: string;
    title: string;
    timestamp: number;
    answers: V2ChatHistoryTurn[];
};
